"use client"
/**
 * Export logic and ExportMenu component for DataTable.
 * Handles resolving export section configs and rendering the export dropdown.
 */
import * as React from 'react'
import { Button } from '../../primitives/button'
import { serializeExport, defaultExportFilename, type PreparedExport } from '@open-mercato/shared/lib/crud/exporters'
import { useT } from '@open-mercato/shared/lib/i18n/context'

export type DataTableExportFormat = 'csv' | 'json' | 'xml' | 'markdown'

export type DataTableExportSectionConfig = {
  title?: string
  description?: string
  getUrl?: (format: DataTableExportFormat) => string
  prepare?: (format: DataTableExportFormat) => Promise<PreparedExport | { prepared: PreparedExport; filename?: string } | null> | PreparedExport | { prepared: PreparedExport; filename?: string } | null
  formats?: DataTableExportFormat[]
  disabled?: boolean
  filename?: (format: DataTableExportFormat) => string
}

export type DataTableExportConfig = {
  label?: string
  disabled?: boolean
  formats?: DataTableExportFormat[]
  getUrl?: (format: DataTableExportFormat) => string
  sections?: DataTableExportSectionConfig[]
  view?: DataTableExportSectionConfig
  full?: DataTableExportSectionConfig
  filename?: (format: DataTableExportFormat) => string
}

export const DEFAULT_EXPORT_FORMATS: DataTableExportFormat[] = ['csv', 'json', 'xml', 'markdown']
export const EXPORT_LABELS: Record<DataTableExportFormat, string> = {
  csv: 'CSV',
  json: 'JSON',
  xml: 'XML',
  markdown: 'Markdown',
}

export type ResolvedExportSection = {
  key: string
  title: string
  description?: string
  formats: DataTableExportFormat[]
  getUrl?: (format: DataTableExportFormat) => string
  prepare?: (format: DataTableExportFormat) => Promise<{ prepared: PreparedExport; filename?: string } | null> | { prepared: PreparedExport; filename?: string } | null
  filename?: (format: DataTableExportFormat) => string
  disabled: boolean
}

export function resolveExportSections(config: DataTableExportConfig | null | undefined): ResolvedExportSection[] {
  if (!config) return []
  const sections: ResolvedExportSection[] = []
  const baseFormats = config.formats && config.formats.length > 0 ? config.formats : DEFAULT_EXPORT_FORMATS
  const addSection = (key: string, section: DataTableExportSectionConfig | undefined | null, fallbackTitle: string) => {
    if (!section || (!section.getUrl && !section.prepare)) return
    const title = section.title?.trim().length ? section.title!.trim() : fallbackTitle
    const seen = new Set<DataTableExportFormat>()
    const formatsSource = section.formats && section.formats.length > 0 ? section.formats : baseFormats
    const formats = formatsSource.filter((format) => {
      if (seen.has(format)) return false
      seen.add(format)
      return true
    })
    if (formats.length === 0) return
    sections.push({
      key,
      title,
      description: section.description,
      formats,
      getUrl: section.getUrl,
      prepare: section.prepare
        ? async (format: DataTableExportFormat) => {
            const result = await section.prepare!(format)
            if (!result) return null
            if ('prepared' in result) return result
            return { prepared: result }
          }
        : undefined,
      filename: section.filename,
      disabled: Boolean(config.disabled || section.disabled),
    })
  }

  const hasExplicitSections = Array.isArray(config.sections) && config.sections.length > 0
  if (!config.view && !config.full && !hasExplicitSections && config.getUrl) {
    addSection('view', { getUrl: config.getUrl, formats: config.formats }, 'Export what you view')
  } else {
    addSection('view', config.view, 'Export what you view')
  }

  if (hasExplicitSections) {
    config.sections!.forEach((section, idx) => {
      addSection(`section-${idx}`, section, section.title?.trim().length ? section.title! : `Export ${idx + 1}`)
    })
  }

  addSection('full', config.full, 'Full data export')
  return sections
}

export function ExportMenu({ config, sections }: { config: DataTableExportConfig; sections: ResolvedExportSection[] }) {
  const t = useT()
  const { label } = config
  const defaultLabel = label ?? t('ui.dataTable.export.label', 'Export')
  const disabled = Boolean(config.disabled)
  const hasSections = sections.length > 0
  const [open, setOpen] = React.useState(false)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open || !hasSections) return
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current && !menuRef.current.contains(target) && buttonRef.current && !buttonRef.current.contains(target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [hasSections, open])

  if (!hasSections) return null

  const handleSelect = async (section: ResolvedExportSection, format: DataTableExportFormat) => {
    try {
      if (section.prepare) {
        const preparedResult = await section.prepare(format)
        if (!preparedResult) return
        const prepared = preparedResult.prepared
        const serialized = serializeExport(prepared, format)
        const filename =
          preparedResult.filename
          ?? section.filename?.(format)
          ?? config.filename?.(format)
          ?? defaultExportFilename(section.title, format)
        if (typeof window !== 'undefined') {
          const blob = new Blob([serialized.body], { type: serialized.contentType })
          const href = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = href
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(href)
        }
      } else if (section.getUrl) {
        const url = section.getUrl(format)
        if (url && typeof window !== 'undefined') {
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      }
    } catch {
      // ignore export errors
    } finally {
      setOpen(false)
    }
  }

  return (
    <div className="relative inline-block">
      <Button
        ref={buttonRef}
        variant="outline"
        size="sm"
        type="button"
        onClick={() => {
          if (disabled) return
          setOpen((prev) => !prev)
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
      >
        {defaultLabel}
      </Button>
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 mt-2 w-60 rounded-md border bg-background py-2 shadow z-20"
        >
          {sections.map((section, idx) => (
            <div key={section.key} className={idx > 0 ? 'mt-2 border-t pt-3' : ''}>
              <div className="px-3">
                <div className="text-xs font-semibold uppercase text-muted-foreground">{section.title}</div>
                {section.description ? (
                  <p className="mt-1 text-xs text-muted-foreground leading-snug">{section.description}</p>
                ) : null}
              </div>
              <div className="mt-2 space-y-1 px-2 pb-1">
                {section.formats.map((format) => (
                  <Button
                    key={`${section.key}-${format}`}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => void handleSelect(section, format)}
                    disabled={section.disabled}
                  >
                    {EXPORT_LABELS[format]}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
